import { Modal } from "@msys/ui";
import { Alert, Link as MuiLink } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Link } from "react-router-dom";
import type { AcceptPendingDocSharePartialFailureStep } from "./useAcceptPendingDocShareOrchestrator.js";

interface Props {
  failedStep: AcceptPendingDocSharePartialFailureStep;
  projectId: string;
  projectTitle: string;
  // The retry is running: Retry shows its progress and the dialog cannot be
  // dismissed until it settles.
  retrying: boolean;
  handleClose: () => void;
  handleRetry: () => Promise<void>;
}

// Partial success of a pending-share acceptance: the project exists but its
// custom-field write or `acceptDocShare` failed, and the message names which.
// Exposes the project link and a retry that repeats only the remaining steps.
export function AcceptPendingDocSharePartialFailureModal({
  failedStep,
  projectId,
  projectTitle,
  retrying,
  handleClose,
  handleRetry,
}: Props) {
  const { t } = useTranslate(["ProjectCreate", "Global"]);
  const dismiss = retrying ? undefined : handleClose;

  return (
    <Modal
      title={t("Create project", { ns: "ProjectCreate" })}
      handleClose={dismiss}
      actionButtons={[
        {
          label: t("Close", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text", disabled: retrying },
        },
        {
          label: t("Retry", { ns: "Global" }),
          handleClick: handleRetry,
          buttonProps: {
            variant: "contained",
            color: "primary",
            loading: retrying,
          },
        },
      ]}
    >
      <Alert severity="warning">
        {failedStep === "customFields"
          ? t(
              "The project was created, but its custom fields could not be saved. The shared document has not been accepted yet.",
              { ns: "ProjectCreate" }
            )
          : t(
              "The project was created, but the shared document could not be accepted.",
              { ns: "ProjectCreate" }
            )}
      </Alert>
      <MuiLink
        component={Link}
        to={`/projects/${projectId}/overview`}
        target="_blank"
      >
        {projectTitle}
      </MuiLink>
    </Modal>
  );
}
